/**
 * PatternDiagram — a small, theme-driven architecture sketch for each deploy catalog pattern.
 *
 * These are hand-authored inline SVGs (not generated images): precise, versioned, and styled off the
 * dashboard's CSS variables so they track the theme. Keyed by the catalog pattern id (see
 * api/src/provisioner/catalog.ts). The custom-cluster sketch reflects the live node/drive selection
 * from the LaunchPanel so what you see matches what you're about to deploy.
 *
 * When a pattern is added/changed in the catalog, add/adjust its case here — a plain <figure> fallback
 * renders for any id without a bespoke sketch, so the picker never breaks.
 */
import type { ReactNode } from 'react';

const VB = '0 0 560 210';

/** Shared arrowhead marker + a couple of reusable primitives keep each sketch terse. */
function Defs() {
  return (
    <defs>
      <marker id="pd-ah" markerWidth="9" markerHeight="9" refX="7" refY="3" orient="auto">
        <path d="M0,0 L7,3 L0,6 Z" className="pd-arrow" />
      </marker>
      <marker id="pd-ah-a" markerWidth="9" markerHeight="9" refX="7" refY="3" orient="auto">
        <path d="M0,0 L7,3 L0,6 Z" className="pd-arrow-a" />
      </marker>
      <marker id="pd-ah-start" markerWidth="9" markerHeight="9" refX="2" refY="3" orient="auto">
        <path d="M9,0 L2,3 L9,6 Z" className="pd-arrow-a" />
      </marker>
    </defs>
  );
}

function Node({ x, y, w = 150, h = 34, title, sub }: { x: number; y: number; w?: number; h?: number; title: string; sub?: string }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx="6" className="pd-node" />
      <text x={x + w / 2} y={y + (sub ? 15 : h / 2 + 4)} textAnchor="middle" className="pd-t">
        {title}
      </text>
      {sub && (
        <text x={x + w / 2} y={y + 27} textAnchor="middle" className="pd-s">
          {sub}
        </text>
      )}
    </g>
  );
}

function Box({ x, y, w, h, title, sub, cls = 'pd-box' }: { x: number; y: number; w: number; h: number; title?: string; sub?: string; cls?: string }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx="7" className={cls} />
      {title && (
        <text x={x + w / 2} y={y + (sub ? 16 : h / 2 + 4)} textAnchor="middle" className="pd-t">
          {title}
        </text>
      )}
      {sub && (
        <text x={x + w / 2} y={y + 30} textAnchor="middle" className="pd-s">
          {sub}
        </text>
      )}
    </g>
  );
}

/** Dashed boundary (an AWS region, a laptop, a VM network) with a top-left label. */
function Zone({ x, y, w, h, label }: { x: number; y: number; w: number; h: number; label: string }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx="8" className="pd-zone" />
      <text x={x + 12} y={y + 18} className="pd-tag">
        {label}
      </text>
    </g>
  );
}

function svg(children: ReactNode) {
  return (
    <svg viewBox={VB} role="img" preserveAspectRatio="xMidYMid meet">
      <Defs />
      {children}
    </svg>
  );
}

/** A little stack of drive marks for a node (custom cluster sketch). */
function Drives({ x, y, n }: { x: number; y: number; n: number }) {
  const marks = Array.from({ length: Math.min(n, 4) }, (_, i) => (
    <rect key={i} x={x + i * 16} y={y} width="12" height="16" rx="2" className="pd-drive" />
  ));
  return <g>{marks}</g>;
}

function X86() {
  return svg(
    <>
      <Box x={16} y={82} w={78} h={40} title="Browser" />
      <line x1={94} y1={102} x2={150} y2={102} className="pd-edge" markerEnd="url(#pd-ah)" />
      <text x={122} y={94} textAnchor="middle" className="pd-s">SG · your IP/32</text>
      <Zone x={150} y={22} w={396} h={168} label="AWS · eu-west-2" />
      <rect x={196} y={54} width={230} height={70} rx="7" className="pd-box" />
      <text x={311} y={75} textAnchor="middle" className="pd-t">EC2 t3.medium · x86_64</text>
      <Node x={216} y={84} w={190} h={30} title="AIStor · Free" />
      <line x1={311} y1={124} x2={311} y2={144} className="pd-edge" markerEnd="url(#pd-ah)" />
      <Box x={256} y={146} w={110} h={40} title="EBS 30 GiB" sub="XFS · persists when stopped" cls="pd-store" />
    </>,
  );
}

function Local() {
  return svg(
    <>
      <Box x={16} y={96} w={78} h={40} title="Browser" />
      <Zone x={150} y={20} w={396} h={176} label="Your laptop · Podman" />
      {/* optional public / SSO branch (top) */}
      <path d="M55,96 C55,60 150,58 206,58" className="pd-tunnel" markerEnd="url(#pd-ah-a)" />
      <Box x={210} y={44} w={224} h={36} title="Cloudflare tunnel → HTTPS" sub="optional: + dashboard OIDC (SSO)" cls="pd-opt" />
      <line x1={322} y1={80} x2={322} y2={104} className="pd-tunnel" markerEnd="url(#pd-ah-a)" />
      {/* private path */}
      <line x1={94} y1={128} x2={210} y2={128} className="pd-edge" markerEnd="url(#pd-ah)" />
      <text x={152} y={120} textAnchor="middle" className="pd-s">localhost</text>
      <rect x={212} y={104} width={210} height={66} rx="7" className="pd-box" />
      <text x={317} y={125} textAnchor="middle" className="pd-t">AIStor container · Free</text>
      <Node x={232} y={134} w={170} h={28} title="AIStor" />
      <Box x={446} y={104} w={90} h={66} title="Podman" sub="volume" cls="pd-store" />
      <line x1={422} y1={137} x2={446} y2={137} className="pd-edge" markerEnd="url(#pd-ah)" />
    </>,
  );
}

function Custom({ nodes, drives }: { nodes: number; drives: number }) {
  const shown = Math.min(Math.max(nodes, 1), 4);
  const total = nodes * drives;
  const w = 108;
  const gap = 14;
  const startX = 300 - (shown * w + (shown - 1) * gap) / 2;
  const boxes = Array.from({ length: shown }, (_, i) => {
    const x = startX + i * (w + gap);
    return (
      <g key={i}>
        <Box x={x} y={64} w={w} h={78} cls="pd-box" />
        <text x={x + w / 2} y={82} textAnchor="middle" className="pd-t">{`node ${i + 1}`}</text>
        <Node x={x + 10} y={90} w={w - 20} h={22} title="AIStor" />
        <Drives x={x + (w - Math.min(drives, 4) * 16) / 2} y={118} n={drives} />
      </g>
    );
  });
  return svg(
    <>
      <Zone x={40} y={26} w={480} h={160} label="Your laptop · Podman" />
      {boxes}
      {nodes > shown && (
        <text x={520} y={110} textAnchor="end" className="pd-s">{`… ×${nodes}`}</text>
      )}
      <text x={280} y={172} textAnchor="middle" className="pd-tag">
        {`${nodes} node${nodes === 1 ? '' : 's'} × ${drives} drive${drives === 1 ? '' : 's'} = ${total} total · ${total >= 4 ? 'erasure coding active' : 'no parity until 4 drives'}`}
      </text>
    </>,
  );
}

function Lab() {
  return svg(
    <>
      <Zone x={20} y={16} w={520} h={188} label="Your laptop · Podman — capabilities lab" />
      {/* S3 load balancer fronts the replication pair (S3 API only; Consoles stay per-cluster) */}
      <Box x={200} y={34} w={160} h={30} title="S3 load balancer" cls="pd-node" />
      <text x={280} y={80} textAnchor="middle" className="pd-s">active-passive · S3 API</text>
      <line x1={232} y1={64} x2={152} y2={90} className="pd-edge" markerEnd="url(#pd-ah)" />
      <line x1={328} y1={64} x2={418} y2={90} className="pd-edge" markerEnd="url(#pd-ah)" />
      {/* the active-active replication pair */}
      <Node x={70} y={90} w={160} h={40} title="AIStor · lab" sub="primary" />
      <Node x={330} y={90} w={160} h={40} title="AIStor · lab2" sub="replication peer" />
      <line x1={230} y1={110} x2={330} y2={110} className="pd-edge-a" markerStart="url(#pd-ah-start)" markerEnd="url(#pd-ah-a)" />
      <text x={280} y={104} textAnchor="middle" className="pd-tag">active-active</text>
      {/* tiering */}
      <line x1={150} y1={130} x2={150} y2={160} className="pd-edge" markerEnd="url(#pd-ah)" />
      <text x={196} y={152} className="pd-s">tiering →</text>
      <Node x={70} y={162} w={160} h={38} title="AIStor · tier" sub="warm-tier target" />
      {/* observability — Prometheus scrapes all three clusters (v3, public); Grafana renders them */}
      <line x1={410} y1={130} x2={410} y2={150} className="pd-edge" strokeDasharray="3 3" markerEnd="url(#pd-ah)" />
      <text x={444} y={146} className="pd-s">scrape v3</text>
      <Box x={330} y={150} w={160} h={48} title="Grafana + Prometheus" sub="metrics · dashboards" cls="pd-box" />
    </>,
  );
}

function MlLab() {
  const w = 128;
  const gap = 22;
  const n = 3;
  const zx = 40;
  const zw = 480;
  const cx = zx + zw / 2;
  const startX = zx + (zw - (n * w + (n - 1) * gap)) / 2; // centre the VM row in the zone
  const xs = Array.from({ length: n }, (_, i) => startX + i * (w + gap));
  return svg(
    <>
      <Zone x={zx} y={22} w={zw} h={168} label="QEMU VMs · bridge network · CA-signed TLS" />
      {xs.map((x, i) => (
        <g key={i}>
          <Box x={x} y={58} w={w} h={80} cls="pd-box" />
          <text x={x + w / 2} y={76} textAnchor="middle" className="pd-t">{`VM ${i + 1}`}</text>
          <Node x={x + 12} y={84} w={w - 24} h={22} title="AIStor" />
          <Box x={x + 12} y={112} w={w - 24} h={18} title="NVMe disk" cls="pd-store" />
        </g>
      ))}
      <text x={cx} y={176} textAnchor="middle" className="pd-tag">
        real nodes · ≈2 GiB RAM/node · cluster's own local URLs
      </text>
    </>,
  );
}

export function PatternDiagram({
  patternId,
  nodeCount = 1,
  drivesPerNode = 1,
}: {
  patternId: string;
  nodeCount?: number;
  drivesPerNode?: number;
}) {
  let body: ReactNode;
  let caption: string;
  switch (patternId) {
    case 'snsd-free-x86':
      body = X86();
      caption = 'One EC2 node on the AIStor Free license; the EBS data volume survives stop/start. Stop it between demos to drop compute to ~$0.';
      break;
    case 'snsd-free-local':
      body = Local();
      caption = 'One AIStor container on the laptop (~$0). Reached privately on localhost, or optionally fronted by the Cloudflare tunnel for a public HTTPS URL — with dashboard-OIDC SSO into the Console.';
      break;
    case 'aistor-local-custom':
      body = <Custom nodes={nodeCount} drives={drivesPerNode} />;
      caption = 'A custom local cluster — pick nodes × drives above. 1×1 is standalone; ≥4 total drives engages erasure coding. Exposure/SSO front the primary node exactly as the fixed local pattern.';
      break;
    case 'aistor-capabilities-lab':
      body = Lab();
      caption = 'A multi-deployment lab: a primary cluster, an active-active site-replication peer, and a warm-tier target, with an S3 load balancer (active-passive) fronting the pair so S3 operations survive a node outage. Consoles stay per-cluster (each with its own SSO), and a built-in Prometheus + Grafana stack scrapes all three deployments. Launch private, public, or public + SSO.';
      break;
    case 'ml-standalone':
      body = MlLab();
      caption = 'A higher-fidelity cluster on real QEMU VMs (via the ml tool): real nodes on a bridge network, NVMe disks, and its own CA-signed TLS. Heavier than the container patterns; Console/S3 are the cluster’s own local URLs.';
      break;
    default:
      return null;
  }
  return (
    <figure className="pattern-figure">
      {body}
      <figcaption>{caption}</figcaption>
    </figure>
  );
}
